import { useEffect, useRef } from 'react';
import {
  EVENEMENT_DONNEES_MODIFIEES,
  TOUS_DOMAINES,
  ecouterAutresOnglets,
} from '../lib/fraicheur';

export const DELAI_RAFRAICHISSEMENT = 300;

/**
 * Recharge un écran quand ses données ont pu changer:
 * - une écriture touchant l'un de `domaines` (ici ou dans un autre onglet,
 *   voir `lib/fraicheur.js`);
 * - le retour de l'onglet au premier plan;
 * - le retour du réseau.
 *
 * Les signaux rapprochés sont regroupés (anti-rebond): un paiement qui touche
 * cinq domaines, ou trois écritures enchaînées, ne déclenchent qu'un seul
 * rechargement. `recharger` reçoit `{ silencieux: true }`: un écran peut s'en
 * servir pour ne pas remplacer son contenu par un squelette de chargement.
 * Le rechargement lui-même ne fait que lire: il ne peut pas relancer la boucle.
 */
export function useRafraichirSur(domaines, recharger, { delai = DELAI_RAFRAICHISSEMENT, actif = true } = {}) {
  const rechargerRef = useRef(recharger);
  rechargerRef.current = recharger;
  const cle = (Array.isArray(domaines) ? domaines : [domaines]).filter(Boolean).join('|');

  useEffect(() => {
    if (!actif || typeof window === 'undefined') return undefined;
    ecouterAutresOnglets();
    const suivis = new Set(cle.split('|').filter(Boolean));
    let minuterie = null;
    let demonte = false;

    const planifier = () => {
      if (minuterie) clearTimeout(minuterie);
      minuterie = setTimeout(() => {
        minuterie = null;
        if (demonte) return;
        try {
          const resultat = rechargerRef.current?.({ silencieux: true });
          if (resultat && typeof resultat.catch === 'function') resultat.catch(() => {});
        } catch (erreur) {
          // L'écran signale lui-même ses échecs de chargement.
        }
      }, delai);
    };

    const surModification = (evenement) => {
      const touches = evenement?.detail?.domaines || [];
      if (
        touches.includes(TOUS_DOMAINES) ||
        suivis.has(TOUS_DOMAINES) ||
        touches.some((domaine) => suivis.has(domaine))
      ) {
        planifier();
      }
    };
    const surVisibilite = () => {
      if (document.visibilityState === 'visible') planifier();
    };

    window.addEventListener(EVENEMENT_DONNEES_MODIFIEES, surModification);
    window.addEventListener('online', planifier);
    document.addEventListener('visibilitychange', surVisibilite);
    return () => {
      demonte = true;
      if (minuterie) clearTimeout(minuterie);
      window.removeEventListener(EVENEMENT_DONNEES_MODIFIEES, surModification);
      window.removeEventListener('online', planifier);
      document.removeEventListener('visibilitychange', surVisibilite);
    };
  }, [cle, delai, actif]);
}

export default useRafraichirSur;
